import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mail, Lock, User, Phone, Eye, EyeOff, LogIn, UserPlus, Cake, Briefcase, ShoppingBag, MapPin, IdCard } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api, authApi, municipiosApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { isReturningUser, markReturningUser } from '../utils/returningUserFlag';
import PasswordStrength, { isPasswordStrong } from './PasswordStrength';
import { formatCpf, validateCpf, stripCpf } from '../utils/cpf';

/**
 * AuthModal — caixa unificada com 3 abas:
 *   • "Sou consumidor"   — cadastro rápido de cliente final
 *   • "Sou empreendedor" — cria conta e leva para o wizard /cadastrar
 *   • "Já tenho conta"   — login
 *
 * Default tab:
 *   1. Prop `initialTab` (consumidor | empreendedor | login)
 *   2. Compat: `initialMode='login'` → login | 'register' → consumidor
 *   3. Cookie/localStorage `ec_returning_user` → login (priorizar quem volta)
 *   4. Padrão → consumidor (estimular cadastro)
 */
export default function AuthModal({ isOpen, onClose, onSuccess, initialMode, initialTab }) {
  const resolveInitialTab = () => {
    if (initialTab === 'consumidor' || initialTab === 'empreendedor' || initialTab === 'login') {
      return initialTab;
    }
    if (initialMode === 'login') return 'login';
    if (initialMode === 'register') return 'consumidor';
    if (isReturningUser()) return 'login';
    return 'consumidor';
  };

  const [tab, setTab] = useState(resolveInitialTab); // consumidor | empreendedor | login
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [form, setForm] = useState({
    email: '', password: '', password_confirm: '', nome: '', telefone: '', cpf: '', data_nascimento: '', municipio_id: '',
  });
  const [errors, setErrors] = useState({});

  // Lista de municípios para o select de empreendedor — lazy load só quando a aba abre.
  const [municipios, setMunicipios] = useState([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);

  useEffect(() => {
    if (!isOpen || tab !== 'empreendedor' || municipios.length > 0) return;
    let cancelled = false;
    setLoadingMunicipios(true);
    municipiosApi.list()
      .then((data) => { if (!cancelled) setMunicipios(Array.isArray(data) ? data : []); })
      .catch(() => { /* select ficará vazio — empreendedor pode preencher no wizard */ })
      .finally(() => { if (!cancelled) setLoadingMunicipios(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab]);

  useEffect(() => {
    if (isOpen) {
      setTab(resolveInitialTab());
      setForm({ email: '', password: '', password_confirm: '', nome: '', telefone: '', cpf: '', data_nascimento: '', municipio_id: '' });
      setErrors({});
      setForgotOpen(false);
      setForgotSent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMode, initialTab]);

  // Google OAuth callback — recebe tokens do backend via URL fragment (#token=...&refresh=...).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('token');
      const refresh = params.get('refresh');
      const needsCompletion = params.get('needs_profile_completion') === '1';
      if (token) {
        handleGoogleToken(token, refresh, needsCompletion);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleToken = async (token, refreshToken, needsCompletion) => {
    setLoading(true);
    try {
      // Persiste o token PRIMEIRO para o axios interceptor conseguir usar
      // no chamado /auth/me abaixo. Depois hidratamos o user via context.
      localStorage.setItem('token', token);
      const user = await authApi.me();
      login(token, user, refreshToken || null);
      markReturningUser();
      toast.success(`Bem-vindo, ${user?.nome || 'Usuário'}!`);
      onSuccess?.();
      onClose();
      if (needsCompletion) {
        navigate('/completar-perfil');
      }
    } catch (err) {
      localStorage.removeItem('token');
      toast.error('Erro no login com Google');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH. Use window.location para o return_to.
    const returnTo = window.location.href.split('#')[0];
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backend}/api/auth/google/start?return_to=${encodeURIComponent(returnTo)}`;
  };

  const validate = () => {
    const e = {};
    if (!form.email) e.email = 'Email obrigatório';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email inválido';
    if (!forgotOpen) {
      if (!form.password) e.password = 'Senha obrigatória';
      else if (form.password.length < 8) e.password = 'Mínimo 8 caracteres';
      else if (tab !== 'login' && !isPasswordStrong(form.password)) {
        e.password = 'Sua senha não atende aos requisitos abaixo';
      }
    }
    if (tab !== 'login' && !forgotOpen) {
      if (!form.nome) e.nome = 'Nome obrigatório';
      if (!form.telefone) e.telefone = 'Telefone obrigatório';
      if (!form.cpf) e.cpf = 'CPF obrigatório';
      else if (!validateCpf(form.cpf)) e.cpf = 'CPF inválido';
      if (!form.password_confirm) e.password_confirm = 'Confirme a senha';
      else if (form.password && form.password !== form.password_confirm) {
        e.password_confirm = 'As senhas não conferem';
      }
      if (form.data_nascimento) {
        const d = new Date(form.data_nascimento);
        const today = new Date();
        if (isNaN(d.getTime())) e.data_nascimento = 'Data inválida';
        else if (d > today) e.data_nascimento = 'Não pode ser no futuro';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) {
      setErrors({ email: 'Informe um email válido' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/v2/auth/forgot-password', { email: form.email });
      setForgotSent(true);
    } catch {
      setForgotSent(true); // Always show success (security)
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (tab === 'login') {
        const res = await api.post('/auth/login', {
          email: form.email,
          password: form.password,
        });
        const data = res.data;
        login(data.access_token, data.user, data.refresh_token);
        markReturningUser();
        toast.success(`Bem-vindo de volta, ${data.user?.nome || 'Usuário'}!`);
        onSuccess?.();
        onClose();
      } else {
        // tab === 'consumidor' OR 'empreendedor' — ambos criam conta de usuário
        const res = await api.post('/v2/auth/register', {
          email: form.email,
          password: form.password,
          nome: form.nome,
          telefone: form.telefone,
          cpf: stripCpf(form.cpf),
          ...(form.data_nascimento ? { data_nascimento: form.data_nascimento } : {}),
        });
        const data = res.data;
        login(data.access_token, data.user, data.refresh_token);
        markReturningUser();
        const isEmpreendedor = tab === 'empreendedor';
        // Save intent context so /verificar-email can resume the right flow.
        if (isEmpreendedor) {
          try {
            sessionStorage.setItem('post_verify_intent', JSON.stringify({
              flow: 'empreendedor',
              municipio_id: form.municipio_id || '',
            }));
          } catch (_) { /* ignore */ }
        } else {
          try { sessionStorage.removeItem('post_verify_intent'); } catch (_) { /* ignore */ }
        }
        toast.success(isEmpreendedor
          ? 'Conta criada! Confirme seu e-mail para cadastrar seu negócio.'
          : 'Conta criada! Confirme seu e-mail para concluir.');
        onSuccess?.();
        onClose();
        // Novo comportamento (iter 227): NÃO redireciona mais para /verificar-email.
        // O usuário fica na home ou vai pro wizard de negócio. O EmailVerificationBanner
        // mostra o aviso persistente com countdown. Após 7 dias, o middleware
        // volta a bloquear. Rotas STRICT (avaliar/comentar/resgatar cupom/editar)
        // já bloqueiam desde o dia 0 mesmo sem verificação.
        if (data.user && data.user.email_verificado === false) {
          localStorage.setItem('pending_verification_email', data.user.email);
          try {
            const preferSms = !!form.telefone;
            localStorage.setItem('pending_verification_channel', preferSms ? 'sms' : 'email');
          } catch (_) { /* ignore */ }
        }
        if (isEmpreendedor) {
          const qs = form.municipio_id
            ? `?intent=empreendedor&municipio_id=${encodeURIComponent(form.municipio_id)}`
            : '?intent=empreendedor';
          navigate(`/cadastrar${qs}`);
        } else {
          // Novo (iter 294): consumidor recém-cadastrado é encaminhado pra
          // tela de instalação do PWA — momento de maior intenção de uso.
          // Flag `?novo=1` mostra banner de boas-vindas na página /instalar.
          // Fluxo empreendedor mantém o wizard de negócio (não entra aqui).
          navigate('/instalar?novo=1');
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao processar';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center overflow-y-auto py-4 sm:py-8 px-4"
      data-testid="auth-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      {/* Modal — flex column com header/tabs fixos e content scrollable */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] my-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#56007F] to-[#7B2FA0] px-6 py-5 text-white flex-shrink-0 relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid="auth-close-btn" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-bold" data-testid="auth-modal-title" id="auth-modal-title">
            {forgotOpen
              ? 'Recuperar senha'
              : tab === 'login' ? 'Entre na sua conta'
              : tab === 'empreendedor' ? 'Anuncie seu negócio'
              : 'Crie sua conta grátis'}
          </h2>
          <p className="text-sm text-white/70 mt-0.5">
            {forgotOpen
              ? 'Enviaremos um link para redefinir sua senha'
              : tab === 'login' ? 'Acesse para resgatar cupons, avaliar e favoritar negócios'
              : tab === 'empreendedor' ? 'Cadastre-se e leve seu negócio para o marketplace da sua cidade'
              : 'Resgate cupons, avalie e favorite negócios locais'}
          </p>
        </div>

        {/* Tabs — escondidas durante recuperação de senha */}
        {!forgotOpen && (
          <div
            className="grid grid-cols-3 border-b border-gray-200 bg-gray-50 flex-shrink-0"
            role="tablist"
            data-testid="auth-tabs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'consumidor'}
              onClick={() => { setTab('consumidor'); setErrors({}); }}
              className={`flex flex-col items-center gap-1 py-3 px-2 text-xs font-semibold transition-colors ${
                tab === 'consumidor'
                  ? 'bg-white text-[#56007F] border-b-2 border-[#56007F] -mb-px'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              }`}
              data-testid="auth-tab-consumidor"
            >
              <ShoppingBag className="w-4 h-4" />
              <span>Sou consumidor</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'empreendedor'}
              onClick={() => { setTab('empreendedor'); setErrors({}); }}
              className={`flex flex-col items-center gap-1 py-3 px-2 text-xs font-semibold transition-colors ${
                tab === 'empreendedor'
                  ? 'bg-white text-[#56007F] border-b-2 border-[#56007F] -mb-px'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              }`}
              data-testid="auth-tab-empreendedor"
            >
              <Briefcase className="w-4 h-4" />
              <span>Sou empreendedor</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'login'}
              onClick={() => { setTab('login'); setErrors({}); }}
              className={`flex flex-col items-center gap-1 py-3 px-2 text-xs font-semibold transition-colors ${
                tab === 'login'
                  ? 'bg-white text-[#56007F] border-b-2 border-[#56007F] -mb-px'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              }`}
              data-testid="auth-tab-login"
            >
              <LogIn className="w-4 h-4" />
              <span>Já tenho conta</span>
            </button>
          </div>
        )}

        {/* Content — scroll interno quando o form ficar mais alto que o viewport */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Forgot password */}
          {forgotOpen && forgotSent ? (
            <div className="text-center py-4 space-y-3" data-testid="forgot-sent">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-800">Email enviado!</h3>
              <p className="text-sm text-gray-500">
                Se o email <strong>{form.email}</strong> estiver cadastrado, você receberá um link para redefinir sua senha.
              </p>
              <p className="text-xs text-gray-400">Verifique também a pasta de spam.</p>
              <Button variant="outline" onClick={() => { setForgotOpen(false); setForgotSent(false); setTab('login'); }} className="mt-2">
                Voltar ao login
              </Button>
            </div>
          ) : forgotOpen ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-gray-600">Informe o email cadastrado e enviaremos um link para criar uma nova senha.</p>
              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Seu email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className={`pl-10 ${errors.email ? 'border-red-400' : ''}`}
                    data-testid="forgot-email-input"
                  />
                </div>
                {errors.email && <p className="text-xs text-red-500 mt-0.5">{errors.email}</p>}
              </div>
              <Button type="submit" className="w-full bg-[#56007F] hover:bg-[#420060]" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Enviar link de recuperação'}
              </Button>
              <p className="text-center text-sm text-gray-500">
                <button onClick={() => { setForgotOpen(false); setTab('login'); }} className="text-[#56007F] font-semibold hover:underline" data-testid="back-to-login">Voltar ao login</button>
              </p>
            </form>
          ) : (
          <>
          {/* Highlight para a aba selecionada */}
          {tab === 'empreendedor' && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
              <Briefcase className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-orange-800">
                Após criar sua conta, vamos te levar para cadastrar seu negócio em poucos passos.
              </p>
            </div>
          )}

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-50 transition-colors text-sm font-medium"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {tab === 'login' ? 'Entrar com Google' : 'Continuar com Google'}
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-400">ou</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {tab !== 'login' && (
              <>
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Seu nome completo"
                      value={form.nome}
                      onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                      className={`pl-10 ${errors.nome ? 'border-red-400' : ''}`}
                      data-testid="auth-nome-input"
                    />
                  </div>
                  {errors.nome && <p className="text-xs text-red-500 mt-0.5">{errors.nome}</p>}
                </div>
                <div>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Telefone (WhatsApp)"
                      value={form.telefone}
                      onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))}
                      className={`pl-10 ${errors.telefone ? 'border-red-400' : ''}`}
                      data-testid="auth-telefone-input"
                    />
                  </div>
                  {errors.telefone && <p className="text-xs text-red-500 mt-0.5">{errors.telefone}</p>}
                </div>
                <div>
                  <div className="relative">
                    <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="CPF"
                      value={form.cpf}
                      onChange={e => setForm(f => ({ ...f, cpf: formatCpf(e.target.value) }))}
                      onBlur={() => {
                        if (form.cpf && !validateCpf(form.cpf)) {
                          setErrors(prev => ({ ...prev, cpf: 'CPF inválido' }));
                        } else {
                          setErrors(prev => { const n = { ...prev }; delete n.cpf; return n; });
                        }
                      }}
                      inputMode="numeric"
                      maxLength={14}
                      className={`pl-10 ${errors.cpf ? 'border-red-400' : ''}`}
                      data-testid="auth-cpf-input"
                    />
                  </div>
                  {errors.cpf && <p className="text-xs text-red-500 mt-0.5">{errors.cpf}</p>}
                </div>
                <div>
                  <div className="relative">
                    <Cake className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="date"
                      placeholder="Data de nascimento (opcional)"
                      value={form.data_nascimento}
                      onChange={e => setForm(f => ({ ...f, data_nascimento: e.target.value }))}
                      className={`pl-10 ${errors.data_nascimento ? 'border-red-400' : ''}`}
                      data-testid="auth-data-nascimento-input"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {tab === 'empreendedor' ? 'Data de nascimento (obrigatória — maior de 18 anos)' : 'Data de nascimento (opcional)'}
                  </p>
                  {errors.data_nascimento && <p className="text-xs text-red-500 mt-0.5">{errors.data_nascimento}</p>}
                </div>

                {tab === 'empreendedor' && (
                  <div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <select
                        value={form.municipio_id}
                        onChange={(e) => setForm(f => ({ ...f, municipio_id: e.target.value }))}
                        disabled={loadingMunicipios}
                        className="w-full pl-10 pr-3 py-2 text-sm rounded-md border border-input bg-white focus:outline-none focus:ring-2 focus:ring-[#56007F] focus:border-transparent disabled:opacity-60"
                        data-testid="auth-municipio-select"
                      >
                        <option value="">
                          {loadingMunicipios ? 'Carregando municípios…' : 'Cidade do seu negócio (opcional)'}
                        </option>
                        {municipios.map((mun) => (
                          <option key={mun.id} value={mun.id}>
                            {mun.nome}{mun.uf ? ` — ${mun.uf}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Adiantamos a sua localização no cadastro do negócio.
                    </p>
                  </div>
                )}
              </>
            )}

            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  placeholder="Seu email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className={`pl-10 ${errors.email ? 'border-red-400' : ''}`}
                  data-testid="auth-email-input"
                />
              </div>
              {errors.email && <p className="text-xs text-red-500 mt-0.5">{errors.email}</p>}
            </div>

            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={tab === 'login' ? 'Sua senha' : 'Crie uma senha forte'}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className={`pl-10 pr-10 ${errors.password ? 'border-red-400' : ''}`}
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-0.5">{errors.password}</p>}
              {tab !== 'login' && (
                <PasswordStrength password={form.password} />
              )}
            </div>

            {tab !== 'login' && (
              <div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirme a senha"
                    value={form.password_confirm}
                    onChange={e => setForm(f => ({ ...f, password_confirm: e.target.value }))}
                    className={`pl-10 ${errors.password_confirm ? 'border-red-400' : ''}`}
                    data-testid="auth-password-confirm-input"
                  />
                </div>
                {errors.password_confirm && <p className="text-xs text-red-500 mt-0.5">{errors.password_confirm}</p>}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#56007F] hover:bg-[#420060] text-white"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : tab === 'login' ? (
                <span className="flex items-center gap-2"><LogIn className="w-4 h-4" /> Entrar</span>
              ) : tab === 'empreendedor' ? (
                <span className="flex items-center gap-2"><Briefcase className="w-4 h-4" /> Criar conta e cadastrar negócio</span>
              ) : (
                <span className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Criar minha conta</span>
              )}
            </Button>

            {tab === 'login' && (
              <p className="text-center">
                <button type="button" onClick={() => setForgotOpen(true)} className="text-sm text-gray-500 hover:text-[#56007F] hover:underline" data-testid="forgot-password-link">
                  Esqueci minha senha
                </button>
              </p>
            )}
          </form>

          {/* Sugestão contextual no rodapé */}
          <p className="text-center text-sm text-gray-500">
            {tab === 'login' ? (
              <>Ainda não tem conta? <button onClick={() => setTab('consumidor')} className="text-[#56007F] font-semibold hover:underline" data-testid="switch-to-register">Cadastre-se grátis</button></>
            ) : (
              <>Já tem conta? <button onClick={() => setTab('login')} className="text-[#56007F] font-semibold hover:underline" data-testid="switch-to-login">Entrar</button></>
            )}
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
